<template>
	<div>
		<!-- NUEVA TABLA DE TOTALES ANUALES -->
		<div class="yearly-summary-table">
			<div class="table-header">
				<h3 class="table-title">Totales Anuales por Cuenta Principal</h3>
				<div class="period-info">
					<span class="period-badge">{{ getYearRange() }}</span>
				</div>
			</div>

			<div class="table-container">
				<div class="table-wrapper">
					<table class="comparison-table">
						<thead>
							<tr>
								<th class="account-column fixed-col-1">Cuenta</th>
								<th class="account-name-column fixed-col-2">Descripción</th>
								<th v-for="year in availableYears" :key="year" class="year-column">
									{{ year }}
								</th>
								<th class="total-column">Total General</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="account in mainAccounts" :key="account.code" class="account-row">
								<td class="account-code fixed-col-1">
									<span class="badge badge-account">{{ account.code }}</span>
								</td>
								<td class="account-name fixed-col-2">{{ account.name }}</td>
								<td v-for="year in availableYears" :key="year" class="balance-cell year-column">
									<span class="balance-value"
										:class="getBalanceClass(getAccountBalanceForYear(account.code, year))">
										{{ formatCurrency(getAccountBalanceForYear(account.code, year)) }}
									</span>
								</td>
								<td class="total-cell total-column">
									<span class="total-value" :class="getBalanceClass(getAccountTotalSumAllYears(account.code))">
										{{ formatCurrency(getAccountTotalSumAllYears(account.code)) }}
									</span>
								</td>
							</tr>
						</tbody>
						<tfoot>
							<tr class="total-row">
								<td class="total-label fixed-col-1">
									<strong>TOTAL</strong>
								</td>
								<td class="total-label-2 fixed-col-2">
									<strong>ANUAL</strong>
								</td>
								<td v-for="year in availableYears" :key="year" class="total-balance year-column">
									<strong class="total-value" :class="getBalanceClass(getTotalForYear(year))">
										{{ formatCurrency(getTotalForYear(year)) }}
									</strong>
								</td>
								<td class="grand-total total-column">
									<strong class="grand-total-value" :class="getBalanceClass(getGrandTotalAllYears())">
										{{ formatCurrency(getGrandTotalAllYears()) }}
									</strong>
								</td>
							</tr>
						</tfoot>
					</table>
				</div>
			</div>
		</div>

		<!-- TABLA ORIGINAL SIN CAMBIOS -->
		<div class="comparison-summary-table">
			<div class="table-header">
				<h3 class="table-title">Comparativo Mensual de Cuentas Principales</h3>
				<div class="period-info">
					<span class="period-badge">{{ getPeriodRange() }}</span>
				</div>
			</div>

			<div class="table-container">
				<div class="table-wrapper">
					<table class="comparison-table">
						<thead>
							<tr>
								<th class="account-column fixed-col-1">Cuenta</th>
								<th class="account-name-column fixed-col-2">Descripción</th>
								<th v-for="period in availablePeriods" :key="`${period.year}-${period.month}`"
									class="period-column">
									{{ getMonthName(period.month) }} {{ period.year }}
								</th>
								<th class="total-column">Suma Total</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="account in mainAccounts" :key="account.code" class="account-row">
								<td class="account-code fixed-col-1">
									<span class="badge badge-account">{{ account.code }}</span>
								</td>
								<td class="account-name fixed-col-2">{{ account.name }}</td>
								<td v-for="period in availablePeriods" :key="`${period.year}-${period.month}`"
									class="balance-cell period-column">
									<span class="balance-value"
										:class="getBalanceClass(getAccountBalanceForPeriod(account.code, period))">
										{{ formatCurrency(getAccountBalanceForPeriod(account.code, period)) }}
									</span>
								</td>
								<td class="total-cell total-column">
									<span class="total-value" :class="getBalanceClass(getAccountTotalSum(account.code))">
										{{ formatCurrency(getAccountTotalSum(account.code)) }}
									</span>
								</td>
							</tr>
						</tbody>
						<tfoot>
							<tr class="total-row">
								<td class="total-label fixed-col-1">
									<strong>TOTAL</strong>
								</td>
								<td class="total-label-2 fixed-col-2">
									<strong>GENERAL</strong>
								</td>
								<td v-for="period in availablePeriods" :key="`${period.year}-${period.month}`"
									class="total-balance period-column">
									<strong class="total-value" :class="getBalanceClass(getTotalForPeriod(period))">
										{{ formatCurrency(getTotalForPeriod(period)) }}
									</strong>
								</td>
								<td class="grand-total total-column">
									<strong class="grand-total-value" :class="getBalanceClass(getGrandTotal())">
										{{ formatCurrency(getGrandTotal()) }}
									</strong>
								</td>
							</tr>
						</tfoot>
					</table>
				</div>
			</div>

			<!-- Summary Cards -->
			<div class="summary-cards">
				<div class="summary-card">
					<div class="summary-icon">📊</div>
					<div class="summary-content">
						<div class="summary-label">Cuentas Analizadas</div>
						<div class="summary-value">{{ mainAccounts.length }}</div>
					</div>
				</div>
				<div class="summary-card">
					<div class="summary-icon">📈</div>
					<div class="summary-content">
						<div class="summary-label">Total General</div>
						<div class="summary-value" :class="getBalanceClass(getGrandTotal())">
							{{ formatCurrency(getGrandTotal()) }}
						</div>
					</div>
				</div>
				<div class="summary-card">
					<div class="summary-icon">📋</div>
					<div class="summary-content">
						<div class="summary-label">Períodos</div>
						<div class="summary-value">{{ availableYears.length }}</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	props: {
		accountsData: {
			type: Array,
			required: true,
		},
	},
	computed: {
		availablePeriods() {
			const periods = [];
			const seenPeriods = new Set();

			this.accountsData.forEach((account) => {
				const key = `${account.year}-${account.month}`;
				if (!seenPeriods.has(key)) {
					seenPeriods.add(key);
					periods.push({ month: account.month, year: account.year });
				}
			});

			return periods.sort((a, b) => {
				if (a.year !== b.year) return a.year - b.year;
				return a.month - b.month;
			});
		},
		availableYears() {
			const years = new Set(); // Usamos Set para valores únicos
			this.accountsData.forEach((account) => {
				years.add(account.year); // Solo agregamos el año
			});
			// Convertimos el Set a Array y ordenamos
			return Array.from(years).sort((a, b) => a - b);
		},
		mainAccounts() {
			// Obtener solo las cuentas de nivel 1 (cuentas principales)
			const level1Accounts = this.accountsData.filter(account => account.level === 1);
			const uniqueAccounts = [];
			const seenCodes = new Set();

			level1Accounts.forEach((account) => {
				if (!seenCodes.has(account.code)) {
					seenCodes.add(account.code);
					uniqueAccounts.push({
						code: account.code,
						name: account.name,
						account: account.account,
					});
				}
			});

			return uniqueAccounts.sort((a, b) => a.code.localeCompare(b.code));
		},
	},
	methods: {
		// NUEVOS MÉTODOS PARA TOTALES ANUALES
		getAccountBalanceForYear(accountCode, year) {
			return this.accountsData
				.filter(acc => acc.code === accountCode && acc.year === year && acc.level === 1)
				.reduce((total, acc) => total + acc.balance, 0);
		},
		getAccountTotalSumAllYears(accountCode) {
			return this.availableYears.reduce(
				(total, year) => total + this.getAccountBalanceForYear(accountCode, year),
				0,
			);
		},
		getTotalForYear(year) {
			return this.mainAccounts.reduce((total, account) =>
				total + this.getAccountBalanceForYear(account.code, year), 0);
		},
		getGrandTotalAllYears() {
			return this.mainAccounts.reduce((total, account) =>
				total + this.getAccountTotalSumAllYears(account.code), 0);
		},
		getYearRange() {
			if (this.availableYears.length === 0) return '';
			const firstYear = this.availableYears[0];
			const lastYear = this.availableYears[this.availableYears.length - 1];

			if (this.availableYears.length === 1) {
				return `${firstYear}`;
			}

			return `${firstYear} - ${lastYear}`;
		},

		// MÉTODOS ORIGINALES SIN CAMBIOS
		getAccountBalanceForPeriod(accountCode, period) {
			const account = this.accountsData.find(acc =>
				acc.code === accountCode &&
				acc.year === period.year &&
				acc.month === period.month &&
				acc.level === 1,
			);
			return account ? account.balance : 0;
		},
		getAccountTotalSum(accountCode) {
			return this.availablePeriods.reduce(
				(total, period) => total + this.getAccountBalanceForPeriod(accountCode, period),
				0,
			);
		},
		getTotalForPeriod(period) {
			return this.mainAccounts.reduce((total, account) =>
				total + this.getAccountBalanceForPeriod(account.code, period), 0);
		},
		getGrandTotal() {
			return this.mainAccounts.reduce((total, account) =>
				total + this.getAccountTotalSum(account.code), 0);
		},
		getPeriodRange() {
			if (this.availablePeriods.length === 0) return '';
			const firstPeriod = this.availablePeriods[0];
			const lastPeriod = this.availablePeriods[this.availablePeriods.length - 1];

			if (this.availablePeriods.length === 1) {
				return `${this.getMonthName(firstPeriod.month)} ${firstPeriod.year}`;
			}

			return `${this.getMonthName(firstPeriod.month)} ${firstPeriod.year} - ${this.getMonthName(lastPeriod.month)} ${lastPeriod.year}`;
		},
		getMonthName(month) {
			const months = [
				'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
				'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
			];
			return months[month - 1] || 'Mes inválido';
		},
		formatCurrency(amount) {
			return new Intl.NumberFormat('es-CO', {
				style: 'currency',
				currency: 'COP',
				minimumFractionDigits: 2,
			}).format(Math.abs(amount));
		},
		getBalanceClass(balance) {
			if (balance > 0) return 'balance-debit';
			if (balance < 0) return 'balance-credit';
			return 'balance-neutral';
		},
	},
};
</script>

<style scoped>
/* ESTILOS PARA LA NUEVA TABLA ANUAL */
.yearly-summary-table {
	font-family: 'Arial', sans-serif;
	border: 1px solid #ddd;
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
	background: #fff;
	margin-bottom: 30px; /* Separación entre las dos tablas */
}

.year-column {
	width: 120px !important;
	min-width: 120px !important;
	max-width: 120px !important;
	text-align: right;
	background: #f8f9ff; /* Color ligeramente diferente para distinguir */
}

/* ESTILOS ORIGINALES SIN CAMBIOS */
.comparison-summary-table {
	font-family: 'Arial', sans-serif;
	border: 1px solid #ddd;
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
	background: #fff;
	margin-bottom: 20px;
}

.table-header {
	background: #f9f9f9;
	padding: 16px 20px;
	border-bottom: 1px solid #eee;
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.table-title {
	font-size: 20px;
	color: #333;
	margin: 0;
}

.period-info {
	display: flex;
	align-items: center;
}

.period-badge {
	background-color: #e7f3ff;
	color: #4a90e2;
	padding: 6px 10px;
	border-radius: 5px;
	font-size: 14px;
}

.table-container {
	position: relative;
	overflow: hidden;
}

.table-wrapper {
	overflow-x: auto;
	overflow-y: visible;
	/* Asegurar que el scroll funcione correctamente */
	-webkit-overflow-scrolling: touch;
}

.comparison-table {
	width: 100%;
	border-collapse: collapse;
	min-width: 800px;
	/* Asegurar que la tabla tenga un layout fijo */
	table-layout: fixed;
}

/* Estilos base para todas las celdas */
.comparison-table th,
.comparison-table td {
	padding: 12px 15px;
	border-bottom: 1px solid #eee;
	white-space: nowrap;
	position: relative;
	/* Asegurar que el contenido no se desborde */
	overflow: hidden;
	text-overflow: ellipsis;
}

/* COLUMNAS FIJAS - Primera columna (Cuenta) */
.fixed-col-1 {
	position: sticky !important;
	left: 0 !important;
	background: white !important;
	z-index: 10 !important;
	width: 100px !important;
	min-width: 100px !important;
	max-width: 100px !important;
	border-right: 2px solid #ddd !important;
	/* Asegurar que se mantenga en su lugar */
	box-shadow: 2px 0 5px rgba(0,0,0,0.1);
}

/* COLUMNAS FIJAS - Segunda columna (Descripción) */
.fixed-col-2 {
	position: sticky !important;
	left: 100px !important; /* Exactamente el ancho de la primera columna */
	background: white !important;
	z-index: 10 !important;
	width: 250px !important;
	min-width: 250px !important;
	max-width: 250px !important;
	border-right: 2px solid #ddd !important;
	/* Asegurar que se mantenga en su lugar */
	box-shadow: 2px 0 5px rgba(0,0,0,0.1);
}

/* COLUMNAS DE PERÍODOS - Ancho consistente */
.period-column {
	width: 140px !important;
	min-width: 140px !important;
	max-width: 140px !important;
	text-align: right;
}

/* COLUMNA TOTAL - Ancho consistente */
.total-column {
	width: 160px !important;
	min-width: 160px !important;
	max-width: 160px !important;
	text-align: right;
	background: #f0f9ff;
}

/* Headers de columnas fijas con mayor z-index */
.comparison-table thead .fixed-col-1,
.comparison-table thead .fixed-col-2 {
	background: #f5f5f5 !important;
	z-index: 15 !important;
}

/* Fila total con columnas fijas */
.total-row .fixed-col-1,
.total-row .fixed-col-2 {
	background: #f0f9ff !important;
	z-index: 12 !important;
}

/* Sombra para indicar scroll en la segunda columna */
.fixed-col-2::after {
	content: '';
	position: absolute;
	top: 0;
	right: -8px;
	width: 8px;
	height: 100%;
	background: linear-gradient(to right, rgba(0, 0, 0, 0.15), transparent);
	pointer-events: none;
	z-index: 1;
}

/* Header sticky */
.comparison-table thead {
	position: sticky;
	top: 0;
	z-index: 5;
}

.comparison-table thead th {
	background: #f5f5f5;
	position: sticky;
	top: 0;
	z-index: 5;
}

/* Estilos específicos para contenido */
.account-code {
	font-family: monospace;
	font-size: 13px;
	text-align: center;
}

.account-name {
	font-weight: 500;
	text-align: left;
	/* Permitir que el texto se ajuste si es muy largo */
	white-space: normal;
	word-wrap: break-word;
	line-height: 1.3;
}

.balance-cell {
	text-align: right;
}

.balance-value {
	display: inline-block;
	padding: 4px 8px;
	border-radius: 4px;
	font-weight: 500;
	font-size: 13px;
}

.balance-debit {
	color: #1e7e46;
	background-color: #f0fdf4;
}

.balance-credit {
	color: #c53030;
	background-color: #fef2f2;
}

.balance-neutral {
	color: #718096;
	background-color: #edf2f7;
}

.total-row {
	background: #f0f9ff;
	font-weight: bold;
}

.total-label,
.total-label-2 {
	padding: 12px 15px;
	text-align: left;
}

.total-balance {
	text-align: right;
	padding: 12px 15px;
}

.badge {
	display: inline-block;
	padding: 0.25em 0.4em;
	font-size: 75%;
	font-weight: 700;
	line-height: 1;
	text-align: center;
	white-space: nowrap;
	vertical-align: baseline;
	border-radius: 0.25rem;
}

.badge-account {
	color: #495057;
	background-color: #e9ecef;
}

.summary-cards {
	display: flex;
	justify-content: space-around;
	padding: 20px;
	background: #f9f9f9;
	border-top: 1px solid #eee;
}

.summary-card {
	background: #fff;
	border-radius: 8px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
	padding: 20px;
	text-align: center;
	width: 30%;
}

.summary-icon {
	font-size: 32px;
	margin-bottom: 10px;
}

.summary-label {
	font-size: 14px;
	color: #777;
	margin-bottom: 5px;
}

.summary-value {
	font-size: 18px;
	font-weight: 600;
	color: #333;
}

.total-cell {
	text-align: right;
	background: #f0f9ff;
	font-weight: 600;
}

.total-value {
	font-weight: 700;
}

.grand-total {
	text-align: right;
	color: white !important;
}

.grand-total-value {
	font-size: 16px;
	color: #02979B !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
	.fixed-col-2 {
		width: 200px !important;
		min-width: 200px !important;
		max-width: 200px !important;
	}
	
	.period-column {
		width: 120px !important;
		min-width: 120px !important;
		max-width: 120px !important;
	}
	
	.total-column {
		width: 140px !important;
		min-width: 140px !important;
		max-width: 140px !important;
	}
	
	.year-column {
		width: 100px !important;
		min-width: 100px !important;
		max-width: 100px !important;
	}
}

/* Fix para navegadores webkit */
@supports (-webkit-appearance: none) {
	.fixed-col-1,
	.fixed-col-2 {
		-webkit-transform: translateZ(0);
		transform: translateZ(0);
	}
}
</style>


// WEBPACK FOOTER //
// src/components/records/comparison-account-node.vue